/* NORTHSHEEP — truthful counts on the native Size filter.
 *
 * Shopify's size facet counts a PRODUCT-level metafield listing every size a
 * product is made in. Stock is per variant and the facet cannot see it, so
 * "XXXXL (1)" stayed on the menu after that rug's XXXXL sold out: the shopper
 * ticked the biggest size, hoping, and got an empty grid (owner, 20 Sep 2026:
 * "it gives people false hopes that there is a big size available").
 *
 * The real numbers come from templates/collection.ns-sizestock.liquid, which
 * walks the whole collection 250 products at a time and counts the sizes with
 * an in-stock variant. A size that nothing has left is removed from the menu
 * rather than shown as (0) — there is nothing to tick. On the search page
 * (23 Sep 2026) the same request reaches templates/search.ns-sizestock.liquid,
 * which counts the search's products the same way.
 *
 * Counted WITHOUT the size filter itself, because a facet's count says what
 * that value would give you, not what the current selection gives. Every
 * other filter (price, colour) is passed along, so the numbers match the grid.
 *
 * Fetched lazily and cached for 15 minutes, same as the Sale check: opening
 * the filter is what pays for it, not every collection page-load.
 */
(function () {
  if (window.__nsFacetSizeCountsLoaded) return;
  window.__nsFacetSizeCountsLoaded = true;

  var PARAM = 'filter.p.m.northsheep.filter_size';
  var TTL = 15 * 60 * 1000;
  var inflight = null; // the PROMISE, not the answer — several openers fire in one tick
  var counts = null;
  var countsKey = null; // which cacheKey() `counts` answers

  function cacheKey() {
    /* Everything that changes the answer, and nothing that does not: the
       collection, and the other filters. Page and sort are irrelevant — the
       template walks every page and order cannot change a count. */
    var p = new URLSearchParams(location.search);
    var keep = [];
    p.forEach(function (v, k) {
      if (k === PARAM || k === 'page' || k === 'sort_by') return;
      /* An empty value selects nothing. The drawer's form sends its blank
         price boxes along ("filter.v.price.gte=&…lte="), and counting them
         made the first size tick look like a different selection: the
         counts in hand were thrown away and the list showed its sold-out
         sizes until a new count arrived (audit, 23 Sep 2026). */
      if (v === '') return;
      keep.push(k + '=' + v);
    });
    keep.sort();
    // v2: counts written before the split-terminator fix were one short.
    return 'northsheep_size_stock2:' + location.pathname + '?' + keep.join('&');
  }

  function fromCache(key) {
    try {
      var raw = sessionStorage.getItem(key);
      if (!raw) return null;
      var o = JSON.parse(raw);
      if (!o || !o.t || Date.now() - o.t > TTL) return null;
      return o.s;
    } catch (e) {
      return null;
    }
  }

  function viewUrl(page) {
    var p = new URLSearchParams(location.search);
    p.delete(PARAM);
    p.delete('sort_by');
    p.set('view', 'ns-sizestock');
    p.set('page', String(page));
    return location.pathname + '?' + p.toString();
  }

  function fetchPage(page) {
    return fetch(viewUrl(page), { headers: { 'X-Requested-With': 'XMLHttpRequest' } })
      .then(function (r) {
        return r.ok ? r.text() : null;
      })
      .then(function (t) {
        if (!t) return null;
        try {
          return JSON.parse(t.trim());
        } catch (e) {
          return null;
        }
      });
  }

  function load() {
    var key = cacheKey();
    if (counts && countsKey === key) return Promise.resolve(counts);
    if (inflight) return inflight;
    var hit = fromCache(key);
    if (hit) {
      counts = hit;
      countsKey = key;
      return Promise.resolve(counts);
    }
    inflight = fetchPage(1)
      .then(function (first) {
        if (!first) return null;
        var pages = first.pages || 1;
        if (pages < 2) return [first];
        var rest = [];
        for (var i = 2; i <= pages; i++) rest.push(fetchPage(i));
        return Promise.all(rest).then(function (more) {
          return [first].concat(more);
        });
      })
      .then(function (all) {
        if (!all) return null;
        /* All pages or nothing (audit, 2 Oct 2026). A page that failed (a 429,
           a 5xx, an unreadable answer) used to be skipped and the short totals
           cached for 15 minutes, so a size whose stock sat on that page counted
           0 and left the Size menu. Shopify's own numbers now stay, and the
           next look asks again. */
        if (all.some(function (o) { return !o; })) return null;
        var total = {};
        all.forEach(function (o) {
          if (!o || !o.s) return;
          Object.keys(o.s).forEach(function (k) {
            total[k] = (total[k] || 0) + o.s[k];
          });
        });
        counts = total;
        countsKey = key;
        try {
          sessionStorage.setItem(key, JSON.stringify({ t: Date.now(), s: total }));
        } catch (e) {}
        return total;
      })
      .catch(function () {
        return null;
      })
      .then(function (v) {
        inflight = null;
        return v;
      });
    return inflight;
  }

  /* The code as the facet stores it — the visible label may now carry a
     centimetre range ("XXL (130 cm)", see ns-size-range), and the value
     attribute is the thing the count is keyed by anyway. */
  function paint(total) {
    if (!total) return;
    var inputs = document.querySelectorAll('input[name="' + PARAM + '"]');
    for (var i = 0; i < inputs.length; i++) {
      var input = inputs[i];
      var code = (input.value || '').toUpperCase();
      if (!(code in total)) continue;
      var n = total[code];
      var row = input.closest('li') || input.closest('label');
      if (!row) continue;

      /* An active value stays put whatever its count says. Removing the row
         the shopper is standing on would leave them filtered with no way to
         see it or clear it. */
      if (n === 0 && !input.checked) {
        /* The hidden ATTRIBUTE alone is not enough. Its `display: none` comes
           from the UA sheet, and the facet rows carry a `display: flex` of
           their own, which is more specific — the row stayed on screen with
           its old count, which looked exactly like the script never ran. */
        row.hidden = true;
        row.style.display = 'none';
        continue;
      }
      row.hidden = false;
      row.style.display = '';

      var label = input.closest('label') || row.querySelector('label');
      if (!label) continue;
      /* ONLY WHAT DIFFERS IS WRITTEN (audit, 23 Sep 2026). This ran on a
         mutation observer, and rebuilding the count text IS a mutation: every
         paint re-armed the observer, which painted again 120ms later — 180
         rewrites of this list in two idle seconds, for as long as the page was
         open. Unchanged text is now left alone, so a repaint that finds the
         list right does nothing and the loop has nothing to feed on. */
      var want = ' (' + n + ')';
      var text = label.querySelector('.facet-checkbox__text');
      if (text) {
        var span = text.querySelector('.facet-checkbox__text-label');
        if (span) {
          var tail = span.nextSibling;
          var alreadyRight = tail && tail.nodeType === 3 && tail.nodeValue === want && !tail.nextSibling;
          if (!alreadyRight) {
            // Rebuild rather than hunt for the text node: Dawn writes the count
            // as a bare "(3)" after the span, with whitespace either side.
            while (text.lastChild && text.lastChild !== span) text.removeChild(text.lastChild);
            text.appendChild(document.createTextNode(want));
          }
        }
      }
      var vh = label.querySelector('.visually-hidden');
      var vhWant = (input.value || '') + want;
      if (vh && vh.textContent !== vhWant) vh.textContent = vhWant;
      input.disabled = false;
      label.classList.remove('disabled');
    }
  }

  function run() {
    if (!document.querySelector('input[name="' + PARAM + '"]')) return;
    load().then(paint);
  }

  /* Opening the filter is what triggers the walk. On a collection page the
     drawer and the desktop details are both possible, so any click inside the
     facets counts, and so does the mobile opener. */
  document.addEventListener(
    'click',
    function (e) {
      var t = e.target;
      if (!t || !t.closest) return;
      if (
        t.closest('#main-collection-filters') ||
        t.closest('#main-search-filters') ||
        t.closest('.mobile-facets__open, .mobile-facets__opener, menu-drawer .mobile-facets')
      ) {
        run();
      }
    },
    true
  );

  // Already filtered by size on arrival: the menu is open in spirit, so paint it.
  function boot() {
    if (location.search.indexOf(PARAM) !== -1) run();
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
  else boot();

  /* Sort and filter replace the facets markup wholesale (Section Rendering
     API), taking the painted counts with them. The numbers themselves are
     still good unless another filter changed, so repaint from what we have
     and let cacheKey decide whether a reload is needed. */
  if (window.MutationObserver) {
    var t = null;
    /* Did this batch redraw a size list? Dawn replaces either the ticked
       group's value list (.facets-wrap) or a whole group; both land inside a
       .js-filter that holds size inputs, or bring such inputs in. */
    var SIZE_INPUT = 'input[name="' + PARAM + '"]';
    var touchesSizes = function (records) {
      for (var i = 0; i < records.length; i++) {
        var r = records[i];
        var el = r.target && r.target.nodeType === 1 ? r.target : r.target && r.target.parentElement;
        var group = el && el.closest ? el.closest('.js-filter') : null;
        if (group && group.querySelector(SIZE_INPUT)) return true;
        for (var j = 0; j < r.addedNodes.length; j++) {
          var a = r.addedNodes[j];
          if (a.nodeType === 1 && (a.matches(SIZE_INPUT) || a.querySelector(SIZE_INPUT))) return true;
        }
      }
      return false;
    };
    var mo = new MutationObserver(function (records) {
      /* THE SIZE LIST WAS JUST REDRAWN — CORRECT IT BEFORE IT IS SEEN (owner,
         23 Sep 2026: "if i click on a size then a fracture of a second i see
         all the sizes even the sold out sizes"). Ticking a size re-renders the
         list from Shopify, sold-out sizes and all, and the correction used to
         wait for the 120ms timer below. A mutation observer runs before the
         browser paints, so painting HERE — from counts that still answer this
         page's other filters (a size tick does not change them; see cacheKey)
         — means the uncorrected list is never drawn. takeRecords drops the
         paint's own changes. */
      if (counts && countsKey && touchesSizes(records) && countsKey === cacheKey()) {
        paint(counts);
        mo.takeRecords();
      }
      clearTimeout(t);
      t = setTimeout(function () {
        if (!document.querySelector(SIZE_INPUT)) return;
        var key = cacheKey();
        if (countsKey !== key) {
          var hit = fromCache(key);
          if (hit) {
            counts = hit;
            countsKey = key;
          } else if (countsKey !== null) {
            /* Another filter moved (a colour, the price): these numbers are for
               a different selection. Count again now — the shopper is in the
               filters — instead of leaving Shopify's numbers, sold-out sizes
               included, until the drawer is next opened. Only after a count
               has been taken once, so a page nobody filters stays as lazy as
               before; a failed count leaves countsKey null and is not retried
               on every change. */
            counts = null;
            countsKey = null;
            run();
            return;
          } else {
            return; // never opened on this page: stays lazy
          }
        }
        paint(counts); // writes nothing when the list is already right
      }, 120);
    });
    try {
      mo.observe(document.documentElement, { childList: true, subtree: true });
    } catch (e) {}
  }
})();
